import { CollectDataStepType } from 'src/constants/collect-data/enums'

/**
 * Compile-time feature gates for the admin onboarding flow editor.
 * Set to `false` to hide UI and block the corresponding interactions.
 */
export const FLOW_EDITOR_FEATURE_FLAGS = {
  /** Show delete control and allow removing steps from the local list. */
  allowDeleteSteps: true,

  /** Drag-and-drop reorder in the step list and up/down in the config header. */
  allowReorderSteps: true,

  /**
   * Step types whose configuration can be edited in the right panel (persisted steps).
   * Types not listed stay read-only unless the step id is `new-…`.
   */
  editableStepTypes: [
    CollectDataStepType.DriverLicense,
    CollectDataStepType.VehicleInsurance,
    CollectDataStepType.EIN,
    CollectDataStepType.InsuranceDeclaration,
    CollectDataStepType.VehicleRegistration,
    CollectDataStepType.Vaccination,
    CollectDataStepType.BGC,
    CollectDataStepType.InsuranceBeneficiary,
    CollectDataStepType.SignContract,
    CollectDataStepType.MVR,
    CollectDataStepType.Form,
    CollectDataStepType.InsuranceCoverage,
    CollectDataStepType.LMSTraining,
    CollectDataStepType.MCNumber,
    CollectDataStepType.DOTNumber,
    CollectDataStepType.PaymentMethod,
    CollectDataStepType.PIICollection,
  ],
  /**
   * Show the rename control in the flow editor header. It opens EditTemplateTitleModal, which
   * saves the new name on confirm through the ops-tools rename endpoint — deliberately its own
   * request, since the name is not expressible in the template `actions` payload (the backend's
   * `CollectDataTemplateUpdateDto` accepts step and step-group events only) and so cannot ride
   * along with "Save changes".
   */
  allowEditTemplateTitle: true,

  /**
   * Show the enabled "New template" action on the customer's Onboarding Templates tab. The create
   * endpoint shipped in para-node#6811; set to `false` to fall back to the disabled button and its
   * tooltip if a deploy lags behind this app.
   */
  allowCreateTemplate: true,

  /**
   * Step types that cannot be added from the Add Step modal (existing template steps are unchanged).
   * Extend this list to gate more types without removing them from STEP_CATEGORIES.
   */
  disabledAddStepTypes: [CollectDataStepType.InsuranceCoverage],

  /** Step types that cannot be removed from the sidebar (e.g. policy-synced insurance rows). */
  nonDeletableStepTypes: [CollectDataStepType.InsuranceCoverage],
} as const

export type FlowEditorFeatureFlagKey = keyof typeof FLOW_EDITOR_FEATURE_FLAGS

export function isFlowEditorAddStepTypeDisabled(stepType: CollectDataStepType): boolean {
  return (
    FLOW_EDITOR_FEATURE_FLAGS.disabledAddStepTypes as readonly CollectDataStepType[]
  ).includes(stepType)
}

export function isFlowEditorStepTypeEditable(stepType: CollectDataStepType): boolean {
  return (FLOW_EDITOR_FEATURE_FLAGS.editableStepTypes as readonly CollectDataStepType[]).includes(
    stepType,
  )
}

export function isFlowEditorStepEditable(step: { id: string; type: CollectDataStepType }): boolean {
  if (isFlowEditorNewlyAddedStepId(step.id)) {
    return true
  }

  return isFlowEditorStepTypeEditable(step.type)
}

export function isFlowEditorStepDeletable(step: { type: CollectDataStepType }): boolean {
  return !(
    FLOW_EDITOR_FEATURE_FLAGS.nonDeletableStepTypes as readonly CollectDataStepType[]
  ).includes(step.type)
}

/** Same contract as persisted `step_created` rows in collect-data template updates. */
export function isFlowEditorNewlyAddedStepId(stepId: string): boolean {
  return stepId.startsWith('new-')
}
